import React, { useCallback, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  NativeSyntheticEvent,
  NativeScrollEvent,
} from 'react-native';
import { RouteProp, useRoute, useNavigation } from '@react-navigation/native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import type { RootStackParamList } from '@/navigation/RootNavigator';
import { REDESIGN_TYPOGRAPHY, REDESIGN_SPACING } from '@/constants/residents/homeConstants';
import { useThemedStyles } from '@/hooks/residents/useThemeColors';
import { IMaintenanceFile } from '@/store/api/maintenanceApi';
import { buildStatusTimeline } from '@/utils/residents/maintenance/statusTimeline';
import { getTicketTitleAndBody } from '@/utils/residents/maintenance';
import { KeyboardSafeView } from '@/components/common/KeyboardSafeView';
import { CloseButton, GlassCard } from '@/components/shared/redesign';
import { AnimatedEntrance } from '@/components/shared/redesign/AnimatedEntrance';
import { useReducedMotion } from '@/hooks/shared/useReducedMotion';
import { MaintenanceImageCarousel } from '@/components/residents/maintenance/shared/MaintenanceImageCarousel';
import { WorkRequestInfoSection } from '@/components/residents/maintenance/shared/WorkRequestInfoSection';
import { ChatCTACard } from '@/components/residents/maintenance/shared/ChatCTACard';
import { StatusTimeline } from '@/components/residents/maintenance/shared/StatusTimeline';
import { useFindConversationBySubject } from '@/hooks/residents/useMessages';

type MaintenanceDetailRouteProp = RouteProp<RootStackParamList, 'MaintenanceDetail'>;

const HP = REDESIGN_SPACING.horizontalPadding;

export const MobileMaintenanceDetailScreen: React.FC = () => {
  const styles = useMaintenanceDetailStyles();
  const route = useRoute<MaintenanceDetailRouteProp>();
  const navigation = useNavigation();
  const insets = useSafeAreaInsets();
  const { ticket } = route.params;
  const { findBySubject, isSearching } = useFindConversationBySubject();
  const reduceMotion = useReducedMotion();

  const timeline = buildStatusTimeline(ticket);
  const images: string[] = ticket.files?.map((f: IMaintenanceFile) => f.fileUrl).filter(Boolean) || [];
  const { title: problemTitle, body: problemBody } = getTicketTitleAndBody(ticket);
  const headerTitle = ticket.maintenanceRequestId ? `WO-${ticket.maintenanceRequestId}` : 'Details';

  // The header hairline appears only once content has scrolled beneath it
  // (native sheet behavior); color-only change, no layout shift.
  const [scrolled, setScrolled] = useState(false);
  const handleScroll = useCallback((e: NativeSyntheticEvent<NativeScrollEvent>) => {
    setScrolled(e.nativeEvent.contentOffset.y > 2);
  }, []);

  const handleClose = () => navigation.goBack();

  const handleStartChat = async () => {
    const subjectPrefix = `WO-${ticket.maintenanceRequestId}`;
    const subject = `${subjectPrefix}: ${ticket.maintenanceDescription?.substring(0, 50) || 'Maintenance Request'}`;
    const existingConversationId = await findBySubject(subjectPrefix);
    if (existingConversationId) {
      (navigation as any).navigate('Conversation', { conversationId: existingConversationId, subject });
    } else {
      (navigation as any).navigate('NewConversation', { initialSubject: subject });
    }
  };

  return (
    <KeyboardSafeView isModal style={styles.container}>
      {/* Full-screen modal card (forVerticalIOS) extends under the status bar
          on both platforms, so pad the top inset unconditionally. */}
      <View style={[styles.inner, { paddingTop: insets.top }]}>
        <View style={[styles.header, scrolled && styles.headerScrolled]}>
          <Text style={styles.headerTitle}>{headerTitle}</Text>
          <CloseButton onPress={handleClose} />
        </View>

        <ScrollView
          style={styles.content}
          contentContainerStyle={[styles.contentContainer, { paddingBottom: Math.max(insets.bottom, 24) }]}
          showsVerticalScrollIndicator={false}
          onScroll={handleScroll}
          scrollEventThrottle={16}
        >
          <AnimatedEntrance index={0} disabled={reduceMotion}>
            <GlassCard padding={20} style={styles.card}>
              <Text style={styles.sectionTitle}>Status</Text>
              <StatusTimeline nodes={timeline} />
            </GlassCard>
          </AnimatedEntrance>

          <AnimatedEntrance index={1} disabled={reduceMotion}>
            <GlassCard padding={20} style={styles.card}>
              <Text style={styles.sectionTitle}>Problem</Text>
              <MaintenanceImageCarousel images={images} variant="mobile" />
              <View style={styles.problem}>
                <Text style={styles.problemTitle}>{problemTitle}</Text>
                {problemBody ? (
                  <Text style={styles.problemText}>{problemBody}</Text>
                ) : null}
              </View>
            </GlassCard>
          </AnimatedEntrance>

          <AnimatedEntrance index={2} disabled={reduceMotion}>
            <GlassCard padding={20}>
              <WorkRequestInfoSection ticket={ticket} variant="mobile" />
            </GlassCard>
          </AnimatedEntrance>
          <AnimatedEntrance index={3} disabled={reduceMotion}>
            <ChatCTACard onPress={handleStartChat} variant="mobile" loading={isSearching} />
          </AnimatedEntrance>
        </ScrollView>
      </View>
    </KeyboardSafeView>
  );
};

const useMaintenanceDetailStyles = () =>
  useThemedStyles(({ colors }) =>
    StyleSheet.create({
      container: { flex: 1, backgroundColor: colors.bg.base },
      inner: { flex: 1 },
      header: {
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingHorizontal: HP,
        // 16 + the 32px close pill matches the previous 20 + 24 header height.
        paddingVertical: 16,
        borderBottomWidth: 1,
        borderBottomColor: 'transparent',
      },
      headerScrolled: {
        borderBottomColor: colors.card.border,
      },
      headerTitle: { ...REDESIGN_TYPOGRAPHY.cardTitleLarge, color: colors.text.primary, lineHeight: 24 },
      content: { flex: 1 },
      contentContainer: { paddingHorizontal: HP, paddingTop: 16, gap: 16 },
      card: { gap: 16 },
      sectionTitle: { ...REDESIGN_TYPOGRAPHY.cardTitleLarge, color: colors.text.primary, lineHeight: 24 },
      problem: { gap: 4 },
      // postTitle (15/600) sits between the 16/600 section title and the
      // 14/400 body — the title must read above its body text.
      problemTitle: { ...REDESIGN_TYPOGRAPHY.postTitle, color: colors.text.primary },
      problemText: { ...REDESIGN_TYPOGRAPHY.body, color: colors.text.muted, lineHeight: 21 },
    }),
  );

export default MobileMaintenanceDetailScreen;
